import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreditCard, Trash2, Plus, Landmark, Check, X } from "lucide-react";

const BRANDS = ["Mastercard", "Debit Mastercard", "Maestro", "Visa", "Visa Debit", "V PAY", "PostFinance Card", "American Express", "TWINT"];

export function CardsManager({ companyId }) {
  const [cards, setCards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ last4: "", brand: "Mastercard", kind: "company", label: "", holder_employee_id: "", alias_of_last4: "", wallet: "apple_pay" });
  const WALLET_LABEL = { apple_pay: "Apple Pay", google_pay: "Google Pay", samsung_pay: "Samsung Pay", wallet: "Wallet" };
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const [c, e] = await Promise.all([
        apiClient.get(`/companies/${companyId}/cards`),
        apiClient.get(`/companies/${companyId}/employees`).catch(() => ({ data: [] })),
      ]);
      setCards(c.data.items || []);
      setEmployees((e.data || []).filter((x) => !x.archived_at));
    } catch { toast.error("Karten konnten nicht geladen werden"); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/cards`, {
        last4: form.last4, brand: form.brand, kind: form.kind === "wallet_alias" ? "company" : form.kind, label: form.label || null,
        alias_of_last4: form.kind === "wallet_alias" ? form.alias_of_last4 : null, wallet: form.kind === "wallet_alias" ? form.wallet : null,
        holder_employee_id: form.holder_employee_id || null,
      });
      const n = r.data?.applied_to_pending || 0;
      toast.success(`Karte …${form.last4} gespeichert${n ? ` · auf ${n} offene Belege im Posteingang angewendet` : ""}`);
      setForm({ last4: "", brand: "Mastercard", kind: "company", label: "", holder_employee_id: "", alias_of_last4: "", wallet: "apple_pay" });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Karte konnte nicht gespeichert werden"); } finally { setBusy(false); }
  };

  const applyCard = async (c) => {
    setBusy(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/cards/${c.id}/apply`);
      const n = r.data?.applied_to_pending || 0;
      toast.success(n ? `Karte …${c.last4} auf ${n} offene Belege angewendet – Warnungen im Posteingang aktualisiert` : `Keine offenen Belege mit Karte …${c.last4}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Anwenden fehlgeschlagen"); } finally { setBusy(false); }
  };

  const remove = async (c) => {
    const who = c.kind === "company" ? "Firmenkarte" : `Privatkarte von ${c.holder_name || "Mitarbeiter"}`;
    if (!window.confirm(`${who} …${c.last4} entfernen?\n\nBereits gebuchte Belege bleiben unverändert. Neue Belege mit dieser Karte werden wieder als «Fremde Karte» gemeldet${c.kind === "employee_private" ? " und nicht mehr automatisch als Spesen erkannt" : ""}.`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/cards/${c.id}`);
      setCards((prev) => prev.filter((x) => x.id !== c.id));
      await loadSuggestions();
    } catch { toast.error("Karte konnte nicht gelöscht werden"); }
  };

  const [suggestions, setSuggestions] = useState([]);
  const [acceptLast4, setAcceptLast4] = useState(null);
  const [acceptLabel, setAcceptLabel] = useState("");
  const loadSuggestions = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/cards/suggestions`);
      setSuggestions(r.data.items || []);
    } catch { setSuggestions([]); }
  }, [companyId]);
  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  const acceptSuggestion = async (s) => {
    setBusy(true);
    try {
      const isAlias = s.suggested_kind === "wallet_alias";
      const r = await apiClient.post(`/companies/${companyId}/cards`, isAlias
        ? { last4: s.last4, brand: s.brand || null, kind: "company", alias_of_last4: s.alias_of_last4, wallet: s.wallet }
        : { last4: s.last4, brand: s.brand || null, kind: "company", label: acceptLabel || `Firmenkarte …${s.last4}` });
      const n = r.data?.applied_to_pending || 0;
      toast.success((isAlias ? `…${s.last4} als ${WALLET_LABEL[s.wallet] || "Wallet"}-Nummer der Karte …${s.alias_of_last4} gespeichert` : `Firmenkarte …${s.last4} ins Register übernommen`) + (n ? ` · auf ${n} offene Belege angewendet` : ""));
      setAcceptLast4(null); setAcceptLabel("");
      await Promise.all([load(), loadSuggestions()]);
    } catch (e) { toast.error(e.response?.data?.detail || "Karte konnte nicht übernommen werden"); } finally { setBusy(false); }
  };
  const dismissSuggestion = async (s) => {
    try {
      await apiClient.post(`/companies/${companyId}/cards/suggestions/${s.last4}/dismiss`);
      setSuggestions((prev) => prev.filter((x) => x.last4 !== s.last4));
    } catch { toast.error("Vorschlag konnte nicht ausgeblendet werden"); }
  };

  return (
    <div className="space-y-3" data-testid="cards-manager">
      <p className="text-xs text-slate-500">
        Firmenkarten werden auf Quittungen erkannt (letzte 4 Ziffern). Privatkarten von Mitarbeitenden markieren den Beleg automatisch als Spesen.
        Karten anderer eigener Firmen lösen eine Warnung «falsche Firma» aus.
      </p>
      <div className="flex flex-wrap gap-3 text-xs" data-testid="cards-summary">
        <span className="rounded-full bg-indigo-50 text-indigo-700 px-2.5 py-1" data-testid="cards-summary-company">{cards.filter((c) => c.kind === "company").length} Firmenkarte{cards.filter((c) => c.kind === "company").length === 1 ? "" : "n"}</span>
        <span className="rounded-full bg-teal-50 text-teal-800 px-2.5 py-1" data-testid="cards-summary-private">{cards.filter((c) => c.kind === "employee_private").length} Privatkarte{cards.filter((c) => c.kind === "employee_private").length === 1 ? "" : "n"} (Spesen)</span>
        <span className="rounded-full bg-slate-100 text-slate-700 px-2.5 py-1">{cards.reduce((a, c) => a + (c.booked_count || 0), 0)} Belege gebucht · {cards.reduce((a, c) => a + (c.open_count || 0), 0)} offen</span>
      </div>
      <ul className="divide-y divide-slate-100 rounded border border-slate-200 bg-white">
        {cards.length === 0 && <li className="px-3 py-2 text-xs text-slate-500" data-testid="cards-empty">Noch keine Karten hinterlegt.</li>}
        {[...cards].sort((a, b) => (a.kind === b.kind ? 0 : a.kind === "company" ? -1 : 1)).map((c) => (
          <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm flex-wrap" data-testid={`card-row-${c.last4}`}>
            <CreditCard className="h-4 w-4 text-slate-500" />
            <span className="font-mono">…{c.last4}</span>
            <span className="text-slate-700">{c.brand || "Karte"}</span>
            <span className={`text-[11px] rounded px-1.5 py-0.5 ${c.kind === "company" ? "bg-indigo-50 text-indigo-700" : "bg-teal-50 text-teal-800"}`}>
              {c.kind === "company" ? "Firmenkarte" : "Privatkarte (Spesen)"}
            </span>
            {c.alias_of_last4 && (
              <span className="text-[11px] rounded px-1.5 py-0.5 bg-slate-100 text-slate-700" title="Gerätenummer aus Apple/Google Pay – auf dem Kassenbon steht diese Nummer, die Bank zeigt die echte Karte" data-testid={`card-wallet-${c.last4}`}>
                {WALLET_LABEL[c.wallet] || "Wallet"} von …{c.alias_of_last4}
              </span>
            )}
            <span className="text-xs text-slate-700 truncate" data-testid={`card-holder-${c.last4}`}>{c.holder_name || c.label || <span className="text-slate-400 italic">kein Inhaber</span>}</span>
            <span className="ml-auto flex items-center gap-3 text-[11px] text-slate-500 tabular-nums" data-testid={`card-stats-${c.last4}`}>
              <span title="Gebuchte Belege mit dieser Karte">{c.booked_count || 0} Beleg{c.booked_count === 1 ? "" : "e"} gebucht{c.open_count ? ` · ${c.open_count} offen` : ""}</span>
              {c.total_amount > 0 && <span title="Summe aller Belege">CHF {Number(c.total_amount).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
              <span title="Letzte Verwendung">{c.last_used ? `zuletzt ${new Date(c.last_used).toLocaleDateString("de-CH")}` : "noch nie verwendet"}</span>
            </span>
            {c.open_count > 0 && (
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={busy} onClick={() => applyCard(c)} title="Karte auf die offenen Belege im Posteingang anwenden (Warnung «Fremde Karte» entfernen)" data-testid={`card-apply-${c.last4}`}>
                <Check className="h-3 w-3 mr-1" />Auf {c.open_count} offene anwenden
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-rose-700 hover:bg-rose-50 hover:text-rose-800" onClick={() => remove(c)} title="Karte aus dem Register entfernen" data-testid={`card-delete-${c.last4}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />Entfernen
            </Button>
          </li>
        ))}
      </ul>
      {suggestions.length > 0 && (
        <div className="rounded border border-indigo-200 bg-indigo-50/60 p-3" data-testid="card-suggestions">
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="h-4 w-4 text-indigo-700" />
            <p className="text-sm font-medium text-indigo-900">Aus dem Bankauszug erkannt</p>
            <span className="text-xs text-indigo-700">{suggestions.length} Karte(n) noch nicht im Register</span>
          </div>
          <ul className="divide-y divide-indigo-100">
            {suggestions.map((s) => (
              <li key={s.last4} className="py-2 text-sm" data-testid={`card-suggestion-${s.last4}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono">…{s.last4}</span>
                  <span className="text-slate-700">{s.brand || "Karte"}</span>
                  {s.suggested_kind === "wallet_alias" && <span className="text-[11px] rounded px-1.5 py-0.5 bg-slate-100 text-slate-700" data-testid={`card-suggestion-wallet-${s.last4}`}>{WALLET_LABEL[s.wallet] || "Wallet"} von …{s.alias_of_last4}?</span>}
                  <span className="text-xs text-slate-600">{s.reason}</span>
                  {s.merchants?.length > 0 && <span className="text-xs text-slate-500 truncate">z.B. {s.merchants.join(", ")}</span>}
                  {s.examples?.length > 0 && <span className="text-xs text-slate-500 truncate" title={s.examples.join("\n")}>z.B. {s.examples[0]}</span>}
                  <div className="ml-auto flex items-center gap-1">
                    {s.suggested_kind === "wallet_alias" ? (
                      <>
                        <Button size="sm" className="h-8" disabled={busy} onClick={() => acceptSuggestion(s)} data-testid={`card-suggestion-alias-${s.last4}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Als {WALLET_LABEL[s.wallet] || "Wallet"}-Nummer von …{s.alias_of_last4} speichern
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => dismissSuggestion(s)} data-testid={`card-suggestion-dismiss-${s.last4}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Ignorieren
                        </Button>
                      </>
                    ) : acceptLast4 === s.last4 ? (
                      <>
                        <Input className="h-8 w-48" autoFocus value={acceptLabel} onChange={(e) => setAcceptLabel(e.target.value)} placeholder="Inhaber / Bezeichnung" data-testid={`card-suggestion-label-${s.last4}`} />
                        <Button size="sm" className="h-8" disabled={busy} onClick={() => acceptSuggestion(s)} data-testid={`card-suggestion-save-${s.last4}`}>Speichern</Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setAcceptLast4(null)}>Abbrechen</Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => { setAcceptLast4(s.last4); setAcceptLabel(""); }} data-testid={`card-suggestion-accept-${s.last4}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Als Firmenkarte übernehmen
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-slate-500" onClick={() => dismissSuggestion(s)} data-testid={`card-suggestion-dismiss-${s.last4}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Ignorieren
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <Input value={form.last4} onChange={(e) => setForm({ ...form, last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} placeholder="Letzte 4 Ziffern" data-testid="card-form-last4" />
        <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="card-form-brand">
          {BRANDS.map((b) => <option key={b}>{b}</option>)}
        </select>
        <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="card-form-kind">
          <option value="company">Firmenkarte</option>
          <option value="employee_private">Privatkarte Mitarbeiter</option>
          <option value="wallet_alias">Apple/Google-Pay-Nummer einer Karte</option>
        </select>
        {form.kind === "company" ? (
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Inhaber / Bezeichnung" data-testid="card-form-label" />
        ) : form.kind === "wallet_alias" ? (
          <div className="flex gap-1">
            <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm flex-1" value={form.alias_of_last4} onChange={(e) => setForm({ ...form, alias_of_last4: e.target.value })} data-testid="card-form-alias-of">
              <option value="">Gehört zu Karte …</option>
              {cards.filter((c) => !c.alias_of_last4).map((c) => <option key={c.id} value={c.last4}>…{c.last4} {c.brand || ""} {c.holder_name || c.label || ""}</option>)}
            </select>
            <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" value={form.wallet} onChange={(e) => setForm({ ...form, wallet: e.target.value })} data-testid="card-form-wallet">
              {Object.entries(WALLET_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        ) : (
          <select className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" value={form.holder_employee_id} onChange={(e) => setForm({ ...form, holder_employee_id: e.target.value })} data-testid="card-form-employee">
            <option value="">Mitarbeiter wählen …</option>
            {employees.map((e) => <option key={e._id || e.id} value={e._id || e.id}>{e.first_name} {e.last_name}</option>)}
          </select>
        )}
        <Button onClick={add} disabled={busy || form.last4.length !== 4 || (form.kind === "employee_private" && !form.holder_employee_id) || (form.kind === "wallet_alias" && !form.alias_of_last4)} data-testid="card-form-submit">
          <Plus className="h-4 w-4 mr-1" /> Karte hinzufügen
        </Button>
      </div>
    </div>
  );
}
